// ============================================================================
// BehaviorIncidentList.tsx
// Section component for displaying the full list of behavior incidents
// Uses DynamicGrid with in-column filters like the incident-list report
// ============================================================================

import { analyticsGridKey } from "../../../../../utils/helpers/analyticsGridKey";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { Header, LayerContent, HeaderContainer } from "../styles";
import { useAnalyticsWrapper } from "../../../../../store/store";
import useFormDataHelpers from "../../../../../hooks/useFormDataHelpers";
import DynamicGrid from "../../../../../components/DynamicGrid/DynamicGrid";
import { behaviorIncidentListCols } from "../../dummyData/colums";

// ============================================================================
// Styled Components
// ============================================================================
const ResponsiveTableContainer = styled.div`
  margin: 32px 0;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Ensure the grid takes full width but allows horizontal scroll */
  & .k-grid {
    min-width: 1200px;
  }
`;

// ============================================================================
// Props Interface
// ============================================================================
interface BehaviorIncidentListProps {
  setClickedIncident: (item: any) => void;
  isFetchingSelectedIncident: boolean;
}

// ============================================================================
// Component
// ============================================================================
const BehaviorIncidentList = ({
  setClickedIncident,
  isFetchingSelectedIncident,
}: BehaviorIncidentListProps) => {
    // ---------------------------------------------------------------------------
    // Helper Hooks
    // ---------------------------------------------------------------------------
    const { getFacilityLabel } = useFormDataHelpers();

    // ---------------------------------------------------------------------------
    // Local State
    // ---------------------------------------------------------------------------
    const [tableData, setTableData] = useState<any[]>([]);

    // ---------------------------------------------------------------------------
    // Global State from Zustand
    // ---------------------------------------------------------------------------
    const { startDate, endDate, behaviorFacility, behaviorAnalysisIncidents } =
      useAnalyticsWrapper((state) => ({
        startDate: state.startDate,
        endDate: state.endDate,
        behaviorFacility: state.behaviorFacility,
        behaviorAnalysisIncidents: state.behaviorAnalysisIncidents,
      }));

    // Derived value for facility name
    const facilityLabel = getFacilityLabel(behaviorFacility);

    // ---------------------------------------------------------------------------
    // Process data when behaviorAnalysisIncidents changes
    // ---------------------------------------------------------------------------
    useEffect(() => {
      if (behaviorAnalysisIncidents !== undefined) {
        const tempData = behaviorAnalysisIncidents?.incidentList || [];
        setTableData(tempData);
      }
    }, [behaviorAnalysisIncidents]);

    const title = "Behavior Incident List";

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    return (
      <LayerContent style={{ marginTop: "10px" }}>
        <HeaderContainer style={{ position: "relative" }}>
          <Header>{title}</Header>
        </HeaderContainer>
        <ResponsiveTableContainer>
          <DynamicGrid
            gridCols={behaviorIncidentListCols()}
              gridData={tableData}
              showTitleBar={true}
              barTitle={facilityLabel}
              includeIncidentLink={true}
              startDate={startDate}
              endDate={endDate}
              reportName="Behavior Incident List"
              viewStateKey={analyticsGridKey(
                "behavior-incident-list",
                startDate,
                endDate,
                facilityLabel
              )}
            />
          </ResponsiveTableContainer>
      </LayerContent>
    );
  };

export default BehaviorIncidentList;